/** leads/api.ts — 리드 모듈 API 래퍼
 * 기존 코드 관례 준수: localStorage 키 'token'(plain string), 200/201만 X-New-Token 갱신(304 캐시 방지)
 */
import type { Candidate, CandidateFilters, CandidateMeta, Capital, CapitalMeta, CapitalCompany, ScanStatus, ScanSchedule, CartInfo, FpNote, FsSummary, Lead, LeadActivity, LeadListFilters, LeadMeta, LeadRowInput, LeadStatus, PortalLead, ListRow, AssignCounts, ProposalData, SendLinkPreview, ProposalSettings, HeldLeads } from './types';
import { keepNewToken } from '../tokenRefresh';

interface ApiResponse<T> { success: boolean; data: T; error?: string }

async function req<T>(method: string, path: string, body?: unknown): Promise<T> {
  const opts: RequestInit = {
    method,
    headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + (localStorage.getItem('token') || '') },
  };
  if (body && method !== 'GET') opts.body = JSON.stringify(body);
  const res = await fetch('/api/leads' + path, opts);
  if (res.status === 200 || res.status === 201) {
    const nt = res.headers.get('X-New-Token');
    keepNewToken(nt);
  }
  const json: ApiResponse<T> = await res.json();
  if (!res.ok || !json.success) throw new Error(json.error || 'API ' + res.status);
  return json.data;
}

// 버그수정(2026-08-11): 슬라이딩 토큰 갱신을 req 외 클라이언트(creq/sreq/xreq/certReq)에도 적용.
//   기존엔 후보/스캔/증자/인증 탭에서만 오래 작업하면 X-New-Token을 못 받아 세션이 만료됐음.
function keepToken(res: Response): void {
  if (res.status === 200 || res.status === 201) { const nt = res.headers.get('X-New-Token'); keepNewToken(nt); }
}

function qs(params: Record<string, string | number | undefined>): string {
  const parts = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== '')
    .map(([k, v]) => k + '=' + encodeURIComponent(String(v)));
  return parts.length ? '?' + parts.join('&') : '';
}

// ── 관리자 ──
export function listLeads(f: LeadListFilters): Promise<{ rows: ListRow[]; total: number; page: number; pageSize: number; counts?: Record<string, number>; assignCounts?: AssignCounts; view: 'company' | 'lead' }> {
  const { invest_years, rounds, opLoss, ...rest } = f;
  return req('GET', '/' + qs({ ...(rest as Record<string, string | number | undefined>),
    invest_years: invest_years && invest_years.length ? invest_years.join(',') : undefined,
    rounds: rounds && rounds.length ? rounds.join(',') : undefined,
    opLoss: opLoss ? '1' : undefined }));
}
/** 링크 보내기 미리보기 — 발송 전 수신번호·문안 확인 (2026-09-10) */
export function sendLinkPreview(id: string): Promise<SendLinkPreview> { return req('GET', '/fp-portals/' + id + '/send-link/preview'); }
export function sendLink(id: string, body: { message?: string; phone?: string }): Promise<{ sent: boolean; to: string; url: string }> { return req('POST', '/fp-portals/' + id + '/send-link', body); }
export function getProposalSettings(): Promise<ProposalSettings> { return req('GET', '/settings/proposal'); }
export function saveProposalSettings(s: ProposalSettings): Promise<ProposalSettings> { return req('PUT', '/settings/proposal', s); }
export function unassignLead(id: string): Promise<{ id: string }> { return req('POST', '/' + id + '/unassign'); }
/** 여러 건 회수 — 관리자가 FP에게 준 DB 회수 (2026-09-10). 배정 상태가 아닌 건은 skipped */
export function unassignBulk(ids: string[], reason?: string): Promise<{ unassigned: number; skipped: number }> { return req('POST', '/unassign-bulk', { ids, reason }); }
export function listFpHeldLeads(id: string): Promise<HeldLeads> { return req('GET', '/fp-portals/' + id + '/leads'); }
export function getMeta(): Promise<LeadMeta> {
  return req('GET', '/meta');
}
export function bulkCreate(payload: { batch_name?: string; batch_memo?: string; rows: LeadRowInput[] }): Promise<{ batch_id: string; created: number; skipped: number }> {
  return req('POST', '/bulk', { lead_type: 'venture_refund', ...payload });
}
export function getLead(id: string): Promise<{ lead: Lead; activities: LeadActivity[]; fp_notes: FpNote[] }> {
  return req('GET', '/' + id);
}
export function updateLead(id: string, patch: Partial<Lead> & { detail?: Partial<Lead['detail']> }): Promise<Lead> {
  return req('PUT', '/' + id, patch);
}
/** 투자 건별(회차) 일괄 교체 — §16 건별 귀속연도 선택 대응 (2026-08-26) */
export function updateLeadRounds(id: string, rounds: { amount: number; date?: string | null }[]): Promise<Lead> {
  return req('PUT', '/' + id + '/rounds', { rounds });
}
/** 배정 시 실시간 갱신 확인 결과 (인증 만료 DB, 2026-09-19) — renewed = 이미 재인증돼 배정하지 않은 회사 · unchecked = 확인 불가(그대로 배정) */
export interface AssignRenewed { lead_id: string; corp_name: string; kind: 'venture' | 'mainbiz' | 'innobiz'; kind_label: string; old_end: string | null; new_end: string }
export interface AssignUnchecked { lead_id: string; corp_name: string; reason: string }
export interface AssignResult { updated: number; /** 배정한 회사 수(2026-10-21 · 정원 = 회사 단위) */ companies?: number; /** 정원이 모자라 못 준 회사 수 */ rejected: number; cart: CartInfo | null; fp: { id: string; name: string; phone: string; kind?: string; portal_url: string; short_url?: string; full_url?: string } | null; renewed?: AssignRenewed[]; unchecked?: AssignUnchecked[]; checked?: number }
export function assignLeads(payload: { lead_ids: string[]; fp_id?: string | null; wm_id?: string | null; branch?: string | null; new_fp?: { name: string; phone: string } }): Promise<AssignResult> {
  return req('POST', '/assign', payload);
}
export function deleteLeads(leadIds: string[]): Promise<{ deleted: number }> {
  return req('POST', '/delete', { lead_ids: leadIds });
}
export function setCartSize(fpId: string, cartSize: number): Promise<{ id: string; cart_size: number }> {
  return req('PUT', '/fp/' + fpId + '/cart-size', { cart_size: cartSize });
}
export function addActivity(id: string, a: { kind: string; content: string; status_to?: LeadStatus | null }): Promise<LeadActivity> {
  return req('POST', '/' + id + '/activities', a);
}
export function convertLead(id: string): Promise<{ id: string; name: string }> {
  return req('POST', '/' + id + '/convert');
}
export function fpPortalLink(fpId: string): Promise<{ url: string; short_url?: string; full_url?: string }> {
  return req('GET', '/fp/' + fpId + '/portal-link');
}
export function rotateFpPortalLink(fpId: string): Promise<{ url: string; short_url?: string; full_url?: string }> {
  return req('POST', '/fp/' + fpId + '/rotate-token');
}

// ── 벤처 후보군 (/api/lead-candidates — 별도 마운트) ──
async function creq<T>(method: string, path: string, body?: unknown): Promise<T> {
  const opts: RequestInit = {
    method,
    headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + (localStorage.getItem('token') || '') },
  };
  if (body && method !== 'GET') opts.body = JSON.stringify(body);
  const res = await fetch('/api/lead-candidates' + path, opts);
  keepToken(res);
  const json: ApiResponse<T> = await res.json();
  if (!res.ok || !json.success) throw new Error(json.error || 'API ' + res.status);
  return json.data;
}
export async function uploadCandidateCsv(file: File): Promise<{ total: number; inserted: number; duplicated: number; enriched: number; corrected: number; rescan: number; skipped: number; source: string; anomalies: import('./types').UploadAnomaly[] }> {
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch('/api/lead-candidates/upload', {
    method: 'POST',
    headers: { Authorization: 'Bearer ' + (localStorage.getItem('token') || '') }, // FormData: Content-Type 자동
    body: fd,
  });
  const json = await res.json();
  if (!res.ok || !json.success) throw new Error(json.error || '업로드 실패');
  return json.data;
}
export async function fetchLeadDmHtml(id: string): Promise<string> {
  const res = await fetch('/api/leads/' + id + '/dm', { headers: { Authorization: 'Bearer ' + (localStorage.getItem('token') || '') } });
  if (!res.ok) { const j = await res.json().catch(() => null); throw new Error((j && j.error) || 'DM 생성 실패'); }
  return res.text();
}

export function listCandidates(f: CandidateFilters): Promise<{ rows: Candidate[]; total: number; page: number; pageSize: number }> {
  const { opLoss, expired_recent, ...rest } = f;
  return creq('GET', '/' + qs({ ...(rest as Record<string, string | number | undefined>), opLoss: opLoss ? '1' : undefined, expired_recent: expired_recent ? '1' : undefined }));
}
export function candidateMeta(): Promise<CandidateMeta> {
  return creq('GET', '/meta');
}
export function promoteCandidate(id: string, body: { biz_no: string; invests: { year: number; amount: number | null }[]; ceo_name?: string; phone?: string; email?: string }): Promise<{ created: number; existed: number; lead_ids: string[] }> {
  return creq('POST', '/' + id + '/promote', body);
}
export function deleteCandidates(ids: string[]): Promise<{ deleted: number }> {
  return creq('POST', '/delete', { candidate_ids: ids });
}

export function enrichCrno(ids?: string[]): Promise<{ scanned: number; found: number; not_found: number; failed: number; filled: number }> {
  return req('POST', '/enrich-crno', ids && ids.length ? { ids } : {});
}

// ── 증자 자동 스캔 (/api/lead-scan) ──
async function sreq<T>(method: string, path: string, body?: unknown): Promise<T> {
  const opts: RequestInit = { method, headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + (localStorage.getItem('token') || '') } };
  if (body && method !== 'GET') opts.body = JSON.stringify(body);
  const res = await fetch('/api/lead-scan' + path, opts);
  keepToken(res);
  const json: ApiResponse<T> = await res.json();
  if (!res.ok || !json.success) throw new Error(json.error || 'API ' + res.status);
  return json.data;
}
export function scanStart(region?: string, sigungu?: string, ids?: string[], mode?: 'full' | 'crno' | 'fs'): Promise<ScanStatus> { return sreq('POST', '/start', { ...(ids && ids.length ? { ids } : { region, sigungu }), ...(mode ? { mode } : {}) }); }
export function scanStatus(): Promise<ScanStatus> { return sreq('GET', '/status'); }
export function scanStop(): Promise<ScanStatus> { return sreq('POST', '/stop'); }
export function scanSchedule(): Promise<ScanSchedule> { return sreq('GET', '/schedule'); }
export function scanScheduleSet(b: { enabled: boolean; region?: string; sigungu?: string; run_hour?: number }): Promise<ScanSchedule> { return sreq('PUT', '/schedule', b); }

export function capitalEnrichBizNo(ids?: string[]): Promise<{ scanned: number; found: number; not_found: number; ambiguous: number; leads: number; failed: number }> {
  return xreq('POST', '/enrich-bizno', ids && ids.length ? { ids } : {}); // 라우트는 /api/capital-increases/enrich-bizno
}

// ── 증자 업체 (/api/capital-increases) ──
async function xreq<T>(method: string, path: string, body?: unknown): Promise<T> {
  const opts: RequestInit = {
    method,
    headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + (localStorage.getItem('token') || '') },
  };
  if (body && method !== 'GET') opts.body = JSON.stringify(body);
  const res = await fetch('/api/capital-increases' + path, opts);
  keepToken(res);
  const json: ApiResponse<T> = await res.json();
  if (!res.ok || !json.success) throw new Error(json.error || 'API ' + res.status);
  return json.data;
}
export function capitalBulk(sourceName: string, rows: unknown[]): Promise<{ inserted: number; skipped: number }> {
  return xreq('POST', '/bulk', { source_name: sourceName, rows });
}
export function listCapitalCompanies(f: { page: number; pageSize: number; q?: string; year?: number; region?: string; sigungu?: string; status?: string }): Promise<{ rows: CapitalCompany[]; total: number; page: number; pageSize: number }> {
  return xreq('GET', '/' + qs({ ...f, view: 'company' }));
}
export function listCapitals(f: { page: number; pageSize: number; q?: string; year?: number; status?: string }): Promise<{ rows: Capital[]; total: number; page: number; pageSize: number }> {
  return xreq('GET', '/' + qs({ ...f }));
}
export function capitalMeta(): Promise<CapitalMeta> { return xreq('GET', '/meta'); }
export function runCapitalMatch(): Promise<{ scanned: number; matched: number; auto_leads: number; existed_links: number; needs_biz_no: number; ambiguous: number }> {
  return xreq('POST', '/match');
}
export function confirmCapital(id: string, bizNo: string): Promise<{ lead_id: string; existed: boolean }> {
  return xreq('POST', '/' + id + '/confirm', { biz_no: bizNo });
}
export function deleteCapitals(ids: string[]): Promise<{ deleted: number }> {
  return xreq('POST', '/delete', { ids });
}

// ── FP 포털 (무인증, 토큰 URL) ──
async function preq<T>(method: string, path: string, body?: unknown): Promise<T> {
  const opts: RequestInit = { method, headers: { 'Content-Type': 'application/json' } };
  if (body && method !== 'GET') opts.body = JSON.stringify(body);
  const res = await fetch('/api/lead-portal' + path, opts);
  const json: ApiResponse<T> = await res.json();
  if (!res.ok || !json.success) throw new Error(json.error || 'API ' + res.status);
  return json.data;
}
export function portalHome(token: string): Promise<{ fp: { name: string; company: string; kind?: string }; insuranceColor: string | null; cart: CartInfo; leads: PortalLead[] }> {
  return preq('GET', '/' + token);
}
export function portalClaim(token: string, leadId: string): Promise<{ claimed: boolean }> {
  return preq('POST', '/' + token + '/claim/' + leadId);
}
export function portalReturn(token: string, leadId: string): Promise<{ returned: boolean }> {
  return preq('POST', '/' + token + '/return/' + leadId);
}
/** 관리자 직배정 건 수락/거절 — 수락하기 탭 (097, 2026-09-10) */
export function portalAccept(token: string, leadId: string): Promise<{ accepted: boolean }> { return preq('POST', '/' + token + '/accept/' + leadId); }
export function portalDecline(token: string, leadId: string, reason?: string): Promise<{ declined: boolean }> { return preq('POST', '/' + token + '/decline/' + leadId, { reason: reason || '' }); }
/** 경정청구 제안서 데이터 (A4 2쪽) */
export function portalProposal(token: string, leadId: string): Promise<ProposalData> { return preq('GET', '/' + token + '/leads/' + leadId + '/proposal'); }
/** 경정청구 제안서 PDF (서버 Chromium 렌더) — 모바일 저장·공유용. inc = Case1·2·3 종합소득금액(만원) (2026-09-19) */
export async function portalProposalPdf(token: string, leadId: string, inc?: string): Promise<{ blob: Blob; filename: string }> {
  const res = await fetch('/api/lead-portal/' + token + '/leads/' + leadId + '/proposal.pdf' + (inc ? '?inc=' + encodeURIComponent(inc) : ''));
  if (!res.ok) { let msg = 'PDF ' + res.status; try { const j = await res.json(); msg = j.error || msg; } catch { /* 본문 없음 */ } throw new Error(msg); }
  const cd = res.headers.get('Content-Disposition') || '';
  const m = cd.match(/filename\*=UTF-8''([^;]+)/);
  return { blob: await res.blob(), filename: m ? decodeURIComponent(m[1]) : 'proposal.pdf' };
}
export interface FpProfile { name: string; title: string; company: string; mobile: string; email: string; tel: string; show_email: boolean; show_tel: boolean; dm_theme?: string }
export function portalProfile(token: string): Promise<FpProfile> { return preq('GET', '/' + token + '/profile'); }
export function portalProfileSave(token: string, p: FpProfile): Promise<{ saved: boolean }> { return preq('PUT', '/' + token + '/profile', p); }
export function portalMeeting(token: string, leadId: string, b: { content: string; manage: string; type: string }): Promise<{ round: number; released: boolean }> { return preq('POST', '/' + token + '/leads/' + leadId + '/meetings', b); }
export function portalDetail(token: string, leadId: string): Promise<{ lead: PortalLead; activities: LeadActivity[] }> {
  return preq('GET', '/' + token + '/leads/' + leadId);
}
export function portalActivity(token: string, leadId: string, a: { kind: string; content: string; status_to?: LeadStatus | null }): Promise<LeadActivity> {
  return preq('POST', '/' + token + '/leads/' + leadId + '/activities', a);
}
export function portalAddNote(token: string, leadId: string, content: string): Promise<FpNote> {
  return preq('POST', '/' + token + '/leads/' + leadId + '/notes', { content });
}
export function portalDeleteNote(token: string, noteId: string): Promise<{ deleted: boolean }> {
  return preq('DELETE', '/' + token + '/notes/' + noteId);
}
/** 요약재무제표 JSON — 법인정보 시트 인라인 표 (v4 2026-09-05) */
export function portalFsSummary(token: string, leadId: string): Promise<FsSummary> {
  return preq('GET', '/' + token + '/leads/' + leadId + '/fs-summary');
}
export function portalSaveContact(token: string, leadId: string, c: { phone?: string; email?: string; ceo_name?: string; note?: string }): Promise<{ phone: string; ceo_name: string }> {
  return preq('PUT', '/' + token + '/leads/' + leadId + '/contact', c);
}

// ── 인증 만료 명단 (/api/cert-candidates) — 메인비즈·이노비즈 ──
export interface CertRow { id: string; kind: string; corp_name: string; ceo_name: string; industry: string; region: string; sigungu?: string; address?: string; estb_dt?: string | null; expire_date: string; biz_no: string; corp_no: string; lead_id: string | null; fs_year?: number | null; fs_sale?: number | null; fs_op?: number | null; renew_status?: string | null; renew_end?: string | null; fp_name?: string; lead_assigned?: boolean }
export interface CertMeta { d30: number; d90: number; d180: number; total: number; regions: string[]; sigungus: { region: string; sigungu: string }[] }
async function certReq<T>(method: string, path: string, body?: unknown): Promise<T> {
  const r = await fetch('/api/cert-candidates' + path, {
    method, headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + (localStorage.getItem('token') || '') },
    body: body ? JSON.stringify(body) : undefined,
  });
  keepToken(r);
  const j = await r.json();
  if (!j.success) throw new Error(j.error || '요청 실패');
  return j.data as T;
}
export function certList(p: { kind: string; months?: number; region?: string; sigungu?: string; q?: string; page?: number; pageSize?: number; assigned?: string }): Promise<{ rows: CertRow[]; total: number; page: number; pageSize: number }> {
  const qs = Object.entries(p).filter(([, v]) => v !== undefined && v !== '' && v !== 0).map(([k, v]) => k + '=' + encodeURIComponent(String(v))).join('&');
  return certReq('GET', '/?' + qs);
}
export function certMeta(kind: string): Promise<CertMeta> { return certReq('GET', '/meta?kind=' + kind); }
export async function certUpload(kind: string, file: File): Promise<{ parsed: number; inserted: number; updated?: number; skipped: number; errors: { line: number; error: string }[] }> { // updated: 엎어쓰기 병합 건수 (2026-08-11)
  const fd = new FormData();
  fd.append('kind', kind); fd.append('file', file);
  const r = await fetch('/api/cert-candidates/upload', { method: 'POST', headers: { Authorization: 'Bearer ' + (localStorage.getItem('token') || '') }, body: fd });
  const j = await r.json();
  if (!j.success) throw new Error(j.error || '업로드 실패');
  return j.data;
}

export interface CertEnrichStatus { running: boolean; kind: string; total: number; scanned: number; hit: number; none: number; ambiguous: number; noCeo: number; errors: number; pausedByQuota: boolean }
export interface CertEnrichSummary { withBiz: number; ambiguous: number; failed: number; pending: number; total: number }
export function certEnrichStart(kind: string): Promise<{ total: number }> { return certReq('POST', '/enrich/start', { kind }); }
export function certEnrichStop(): Promise<Record<string, never>> { return certReq('POST', '/enrich/stop', {}); }
export function certEnrichStatus(): Promise<CertEnrichStatus> { return certReq('GET', '/enrich/status'); }
export function renewCertStart(kind: string, ids?: string[]): Promise<{ total: number }> { return certReq('POST', '/renew/start', { kind, ...(ids && ids.length ? { ids } : {}) }); } // ids: 선택 업체만 (2026-08-11)
export function renewCertStop(): Promise<Record<string, never>> { return certReq('POST', '/renew/stop', {}); }
export function renewCertStatus(): Promise<{ running: boolean; kind: string; total: number; checked: number; renewed: number; active: number; errors: number; pausedByQuota: boolean }> { return certReq('GET', '/renew/status'); }
export function renewCertRow(kind: string, id: string): Promise<{ status: string; startDate?: string | null; endDate: string | null }> { return certReq('POST', '/' + kind + '/rows/' + id + '/renew', {}); } // V3: 유효 시작일 포함 (2026-08-11)
// 벤처 갱신확인 (lead-candidates 라우트)
export function renewVentureStart(ids?: string[]): Promise<{ total: number }> { return creq('POST', '/renew/start', ids && ids.length ? { ids } : {}); } // ids: 선택 업체만 (2026-08-11)
export function renewVentureStatus(): Promise<{ running: boolean; total: number; checked: number; renewed: number; active: number; errors: number; pausedByQuota: boolean }> { return creq('GET', '/renew/status'); }
export function renewVentureRow(id: string): Promise<{ status: string; startDate?: string | null; endDate: string | null }> { return creq('POST', '/' + id + '/renew', {}); } // V3: 유효 시작일 포함 (2026-08-11)
export function certEnrichSummary(kind: string): Promise<CertEnrichSummary> { return certReq('GET', '/enrich/summary?kind=' + kind); }

export function promoteExpiryCandidates(ids: string[]): Promise<{ leadIds: string[]; failed: { id: string; corp_name: string; reason: string }[] }> {
  return fetch('/api/lead-candidates/promote-expiry', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + (localStorage.getItem('token') || '') }, body: JSON.stringify({ ids }) })
    .then((r) => r.json()).then((j) => { if (!j.success) throw new Error(j.error || '승격 실패'); return j.data; });
}
export function certPromote(kind: string, ids: string[]): Promise<{ leadIds: string[]; failed: { id: string; corp_name: string; reason: string }[] }> {
  return certReq('POST', '/promote', { kind, ids });
}

/* ── 신규법인 리스트 (2026-10-17) — 신설법인 명단 · 벤처 여부(중소벤처24) · 벤처확인 권유 DM · 배정 ── */
export type NewCorpVenture = 'valid' | 'past' | 'none';
export interface NewCorpRow {
  id: string; bizNo: string; corpNo: string; corpName: string; ceoName: string; region: string; sigungu: string; address: string;
  industry: string; corpType: string; phone: string; estbYear: number | null; fsYear: number | null; fsSale: number | null; fsOp: number | null; fsCapital: number | null; fsResult: string;
  ventureFrom: string | null; ventureTo: string | null; ventureState: NewCorpVenture; ventureChecked: boolean; ventureCheckedAt: string | null;
  leadId: string | null; fpName?: string; assigned?: boolean;
}
export interface NewCorpMeta { regions: string[]; sigungus: { region: string; sigungu: string }[]; years: number[]; total: number; none: number; venture: number; unchecked: number }
async function ncReq<T>(method: string, path: string, body?: unknown): Promise<T> {
  const r = await fetch('/api/new-corps' + path, {
    method, headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + (localStorage.getItem('token') || '') },
    body: body ? JSON.stringify(body) : undefined,
  });
  keepToken(r);
  const j = await r.json().catch(() => null);
  if (!j || !j.success) throw new Error((j && j.error) || '요청 실패');
  return j.data as T;
}
export function newCorpList(p: { page: number; pageSize: number; q?: string; region?: string; sigungu?: string; year?: number; venture?: string; assigned?: string }): Promise<{ rows: NewCorpRow[]; total: number }> {
  const qs = new URLSearchParams();
  Object.entries(p).forEach(([k, v]) => { if (v !== undefined && v !== '' && v !== 0) qs.set(k, String(v)); });
  return ncReq('GET', '?' + qs.toString());
}
export function newCorpMeta(): Promise<NewCorpMeta> { return ncReq('GET', '/meta'); }
export function newCorpPromote(ids: string[]): Promise<{ leadIds: string[]; failed: { id: string; corp_name: string; reason: string }[] }> { return ncReq('POST', '/promote', { ids }); }
export function newCorpVentureCheck(ids: string[]): Promise<{ valid: number; none: number; error: number; skipped: number }> { return ncReq('POST', '/venture-check', { ids }); }
export async function newCorpUpload(file: File): Promise<{ total: number; parsed: number; inserted: number; updated: number; errorCount: number; errors: { line: number; error: string }[] }> {
  const fd = new FormData();
  fd.append('file', file);
  const r = await fetch('/api/new-corps/upload', { method: 'POST', headers: { Authorization: 'Bearer ' + (localStorage.getItem('token') || '') }, body: fd });
  keepToken(r);
  const j = await r.json().catch(() => null);
  if (!j || !j.success) throw new Error((j && j.error) || '업로드 실패');
  return j.data;
}
